import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Send, Download, Copy, CreditCard, Eye, Pencil, CornerDownRight } from 'lucide-react';
import { useInvoice, useInvoices } from '@/composables/financial/useInvoices';
import { useAccounts } from '@/composables/financial/useAccounts';
import { usePayments } from '@/composables/financial/usePayments';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { invoiceService } from '@/services/invoice.service';
import { paymentSchema, type PaymentFormData } from '@/lib/validators';
import { PAYMENT_METHODS } from '@/lib/constants';
import { usePdfActions } from '@/composables/shared/usePdfActions';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { groupItems } from '@/lib/documentItems';
import { taxLines } from '@/lib/tax';
import { toast } from 'sonner';

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invoice, isLoading } = useInvoice(id!);
  const { settings } = useOrganizationSettings();
  const { sendInvoice, duplicateInvoice } = useInvoices();
  const { accounts } = useAccounts();
  const { createPayment } = usePayments();
  const [showRecordPayment, setShowRecordPayment] = useState(false);

  const remaining = invoice ? Math.max(0, Number(invoice.totalAmount) - Number(invoice.paidAmount)) : 0;
  // Lump-sum: the items are description-only, so the money columns are hidden.
  const isLumpSum = invoice?.pricingMode === 'LUMP_SUM';

  const form = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount: 0,
      currency: 'USD',
      paymentMethod: 'BANK_TRANSFER',
      referenceNumber: '',
      paymentDate: new Date().toISOString().slice(0, 10),
      notes: '',
      invoiceId: id || '',
      accountId: '',
    },
  });

  // Pre-fill amount with the remaining balance + currency when the dialog opens
  useEffect(() => {
    if (showRecordPayment && invoice) {
      form.reset({
        amount: remaining,
        currency: invoice.currency as PaymentFormData['currency'],
        paymentMethod: 'BANK_TRANSFER',
        referenceNumber: '',
        paymentDate: new Date().toISOString().slice(0, 10),
        notes: '',
        invoiceId: invoice.id,
        accountId: accounts[0]?.id || '',
      });
    }
  }, [showRecordPayment, invoice, remaining, accounts, form]);

  const pdf = usePdfActions();
  const handleDownloadPdf = () => pdf.download(() => invoiceService.downloadPdf(id!), `invoice-${invoice?.invoiceNumber || id}.pdf`);
  const handleViewPdf = () => pdf.view(() => invoiceService.downloadPdf(id!));

  const handleRecordPayment = (data: PaymentFormData) => {
    if (!invoice) return;
    if (data.amount <= 0) { toast.error('Amount must be greater than zero'); return; }
    if (data.amount > remaining + 0.001) {
      toast.error(`Amount cannot exceed the balance due of ${formatCurrency(remaining, invoice.currency)}`);
      return;
    }
    if (!data.accountId) { toast.error('Account is required'); return; }

    createPayment.mutate(
      {
        ...data,
        invoiceId: invoice.id,
        currency: invoice.currency as PaymentFormData['currency'],
      } as Parameters<typeof createPayment.mutate>[0],
      {
        onSuccess: () => setShowRecordPayment(false),
      },
    );
  };

  if (isLoading) return <LoadingSpinner />;
  if (!invoice) return <div className="p-8 text-center">Invoice not found</div>;

  const canRecordPayment = !['PAID', 'CANCELLED', 'REFUNDED'].includes(invoice.status) && remaining > 0;
  // Always editable except for cancelled. Backend strips out items/totals/tax
  // when payments exist, so the form will lock those fields too.
  const canEdit = invoice.status !== 'CANCELLED';

  return (
    <PageContainer backTo
      title={`Invoice ${invoice.invoiceNumber}`}
      actions={
        <>
          <Button variant="outline" onClick={handleViewPdf} loading={pdf.isViewing}><Eye className="mr-2 h-4 w-4" />View PDF</Button>
          <Button variant="outline" onClick={handleDownloadPdf} loading={pdf.isDownloading}><Download className="mr-2 h-4 w-4" />Download</Button>
          {canEdit && <Button variant="outline" onClick={() => navigate(`/financial/invoices/${invoice.id}/edit`)}><Pencil className="mr-2 h-4 w-4" />Edit</Button>}
          {invoice.status === 'DRAFT' && <Button onClick={() => sendInvoice.mutate(invoice.id)}><Send className="mr-2 h-4 w-4" />Send</Button>}
          <Button variant="outline" onClick={() => duplicateInvoice.mutate(invoice.id)}><Copy className="mr-2 h-4 w-4" />Duplicate</Button>
          {canRecordPayment && (
            <Button onClick={() => setShowRecordPayment(true)}><CreditCard className="mr-2 h-4 w-4" />Record Payment</Button>
          )}
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Invoice Info</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={invoice.status} type="invoice" /></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-bold text-lg">{formatCurrency(invoice.totalAmount, invoice.currency)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span>{formatCurrency(invoice.paidAmount, invoice.currency)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Balance Due</span><span className={remaining > 0 ? 'font-semibold' : ''}>{formatCurrency(remaining, invoice.currency)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Issue Date</span><span>{formatDate(invoice.issueDate)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Due Date</span><span>{formatDate(invoice.dueDate)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="text-right">
              {invoice.billingCompanyName && <div className="font-medium">{invoice.billingCompanyName}</div>}
              <div className={invoice.billingCompanyName ? 'text-xs text-muted-foreground' : ''}>{invoice.billingName || '-'}</div>
            </span></div>
            {invoice.billingTaxId && (
              <div className="flex justify-between"><span className="text-muted-foreground">Client GSTIN</span><span className="font-mono text-sm">{invoice.billingTaxId}</span></div>
            )}
            {invoice.convertedFromQuotation && (
              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-muted-foreground">Billed against</span>
                <Button variant="link" size="sm" className="h-auto p-0" onClick={() => navigate(`/financial/quotations/${invoice.convertedFromQuotation!.id}`)}>
                  {invoice.convertedFromQuotation.quotationNumber} →
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    {!isLumpSum && <>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupItems(invoice.items).map(({ item, children }) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.description}
                        {children.map((c) => (
                          <div key={c.id} className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><CornerDownRight className="h-3 w-3 shrink-0" />{c.description}</div>
                        ))}
                      </TableCell>
                      {!isLumpSum && <>
                        <TableCell className="text-right align-top">{item.quantity}</TableCell>
                        <TableCell className="text-right align-top">{formatCurrency(item.unitPrice, invoice.currency)}</TableCell>
                        <TableCell className="text-right align-top">{formatCurrency(item.amount, invoice.currency)}</TableCell>
                      </>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 space-y-1 text-right">
                <p className="text-sm">Subtotal: {formatCurrency(invoice.subtotal, invoice.currency)}</p>
                {taxLines(invoice, settings?.taxLabel).map((line) => (
                  <p key={line.label} className="text-sm">{line.label}: {formatCurrency(line.amount, invoice.currency)}</p>
                ))}
                {Number(invoice.discountAmount) > 0 && <p className="text-sm">Discount: -{formatCurrency(invoice.discountAmount, invoice.currency)}</p>}
                <p className="text-lg font-bold">Total: {formatCurrency(invoice.totalAmount, invoice.currency)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Payment history */}
          {invoice.payments && invoice.payments.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Payments</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{formatDate(p.paymentDate)}</TableCell>
                        <TableCell>{p.paymentMethod.replace(/_/g, ' ')}</TableCell>
                        <TableCell className="text-muted-foreground">{p.referenceNumber || '-'}</TableCell>
                        <TableCell className="text-muted-foreground whitespace-pre-wrap max-w-xs">{p.notes || '-'}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(p.amount, p.currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showRecordPayment} onOpenChange={setShowRecordPayment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(handleRecordPayment)} className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Balance due: <span className="font-medium text-foreground">{formatCurrency(remaining, invoice.currency)}</span>.
              Enter the full amount to mark the invoice paid, or a smaller amount for a partial payment.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <FormField form={form} name="amount" label="Amount" type="decimal" required />
              <FormDatePicker form={form} name="paymentDate" label="Payment Date" required />
            </div>
            <FormSelect form={form} name="accountId" label="Receiving Account" options={accounts.map(a => ({ label: `${a.name} (${a.type.replace(/_/g, ' ')})`, value: a.id }))} required />
            <FormSelect form={form} name="paymentMethod" label="Method" options={PAYMENT_METHODS.map(m => ({ label: m.replace(/_/g, ' '), value: m }))} required />
            <FormField form={form} name="referenceNumber" label="Reference / Txn ID" />
            <FormTextarea form={form} name="notes" label="Notes" />
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setShowRecordPayment(false)}>Cancel</Button>
              <Button type="submit" disabled={createPayment.isPending}>{createPayment.isPending ? 'Recording…' : 'Record Payment'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
